// Pasting and dropping screenshots into a Markdown field.
//
// Shared because there are three places you write feedback — the give-feedback
// form, the edit form, and the comment box — and a screenshot should behave the
// same in all of them.
//
// The upload is asynchronous and takes a couple of seconds, so this follows
// GitHub's pattern: a placeholder goes in at the cursor immediately, and is
// swapped for the real Markdown when the URL comes back. That keeps the caret
// where the person left it and lets them carry on typing while it uploads.

import { api } from './api.js';

/**
 * What the portal accepts. Deliberately narrower than d3-assets, which also
 * takes SVG — these files are served from a public CDN and rendered in a
 * browser, and an SVG is a script-execution vector. The API enforces this too;
 * checking here just means a clearer refusal than a 422.
 */
export const ATTACHABLE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Matches the API's own limit, so an oversized file fails before the round trip. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * The image files carried by a paste or drop, ignoring everything else.
 *
 * A clipboard paste of a screenshot has no filename — the item is just image
 * bytes — so one is invented. Pasting text still carries an `items` list, which
 * is why the type check matters rather than just the length.
 * @param {ClipboardEvent|DragEvent} event
 * @returns {File[]}
 */
export function imageFilesFrom(event) {
    const source = event.clipboardData ?? event.dataTransfer;
    if (!source) return [];

    const files = [];
    for (const file of source.files ?? []) {
        if (ATTACHABLE_TYPES.includes(file.type)) files.push(file);
    }
    if (files.length > 0) return files;

    // Clipboard images arrive as items rather than files in some browsers.
    for (const item of source.items ?? []) {
        if (item.kind !== 'file' || !ATTACHABLE_TYPES.includes(item.type)) continue;
        const file = item.getAsFile();
        if (file) files.push(file);
    }
    return files;
}

/**
 * A stable placeholder for one in-flight upload. It carries a unique id because
 * two screenshots can be uploading at once and the second must not overwrite the
 * first's marker.
 * @param {string} id
 * @param {string} name
 */
export function uploadPlaceholder(id, name) {
    return `![uploading ${name}… ${id}]()`;
}

/**
 * Insert text at the caret, returning the new value and where the caret should
 * land. Does not touch the element: the caller owns its state, and in Lit the
 * value is re-rendered from that.
 * @param {string} value
 * @param {number} start
 * @param {number} end
 * @param {string} insert
 */
export function insertAt(value, start, end, insert) {
    const before = value.slice(0, start);
    const after = value.slice(end);
    // Keep the inserted image on its own line: Markdown treats a single newline
    // inside a paragraph as a line break, so an image jammed onto the end of a
    // sentence renders mid-sentence.
    const prefix = before === '' || before.endsWith('\n') ? '' : '\n';
    const text = `${prefix}${insert}\n`;
    return { value: `${before}${text}${after}`, caret: before.length + text.length };
}

/**
 * Upload one image and hand back the Markdown that renders it.
 *
 * Rejects oversized files locally rather than sending them, since the round trip
 * for a 40MB screenshot is a slow way to be told no.
 * @param {File} file
 * @returns {Promise<string>} the Markdown, e.g. `![name](url)`
 */
export async function uploadImage(file) {
    if (file.size > MAX_ATTACHMENT_BYTES) {
        throw new Error('That image is too large (10MB maximum).');
    }
    const name = file.name || 'screenshot.png';
    const result = await api.postFile('/attachments', file, name);
    if (!result?.url) throw new Error('The image uploaded but no URL came back.');
    return `![${name}](${result.url})`;
}
